import type { EEGDataPoint, PPGDataPoint, ACCDataPoint } from './SimpleCircularBuffer';
import type { ProcessedEEGData } from '../types/eeg';
import type { ProcessedPPGData, ProcessedACCData } from '../types/processed-data';
import JSZip from 'jszip';
import { createSessionStartTime, type TimezoneType } from './timeUtils';
import { createLogger } from "./logger";


const logger = createLogger("SessionMgr");

/**
 * 성능 모니터링 유틸리티
 */
class PerformanceMonitor {
  private static instance: PerformanceMonitor;
  private metrics: Map<string, {
    totalTime: number;
    executionCount: number;
    maxTime: number;
    lastTime: number;
  }> = new Map();

  static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }

  startTimer(label: string): () => void {
    const startTime = performance.now();
    
    return () => {
      const endTime = performance.now();
      const executionTime = endTime - startTime;
      
      this.recordExecution(label, executionTime);
      
      // 성능 경고 (50ms 이상)
      if (executionTime > 50) {
        logger.warn(`⚠️ ${label} took ${executionTime.toFixed(2)}ms`);
      }
    };
  }

  private recordExecution(label: string, executionTime: number): void {
    const existing = this.metrics.get(label) || {
      totalTime: 0,
      executionCount: 0,
      maxTime: 0,
      lastTime: 0
    };

    existing.totalTime += executionTime;
    existing.executionCount++;
    existing.maxTime = Math.max(existing.maxTime, executionTime);
    existing.lastTime = executionTime;

    this.metrics.set(label, existing);
  }

  getMetrics(label: string) {
    const metric = this.metrics.get(label);
    if (!metric) return null;

    return {
      averageTime: metric.totalTime / metric.executionCount,
      maxTime: metric.maxTime,
      lastTime: metric.lastTime,
      executionCount: metric.executionCount
    };
  }

  getAllMetrics() {
    const result: Record<string, any> = {};
    this.metrics.forEach((value, key) => {
      result[key] = {
        averageTime: value.totalTime / value.executionCount,
        maxTime: value.maxTime,
        lastTime: value.lastTime,
        executionCount: value.executionCount
      };
    });
    return result;
  }

  logPerformanceReport(): void {
    console.group('📊 Performance Report');
    this.metrics.forEach((value, key) => {
      const avg = value.totalTime / value.executionCount;
      logger.debug(`${key}: avg=${avg.toFixed(2)}ms, max=${value.maxTime.toFixed(2)}ms, count=${value.executionCount}`);
    });
    console.groupEnd();
  }
}

// 전역 성능 모니터 인스턴스
const performanceMonitor = PerformanceMonitor.getInstance();

/**
 * IndexedDB 기반 세션 저장소 래퍼.
 *
 * localStorage(5~10MB 한계)를 대체해 대용량 EEG 녹화 데이터를 안전하게 저장.
 * 'metadata' object store: 세션 메타데이터 배열 한 건(key='all').
 * 'sessions' object store: 세션 본문 (key=sessionId).
 */
class SessionDB {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;
  private readonly DB_NAME = 'linkband_sessions';
  private readonly DB_VERSION = 1;
  private readonly STORE_SESSIONS = 'sessions';
  private readonly STORE_METADATA = 'metadata';

  init(): Promise<void> {
    if (this.initPromise) return this.initPromise;
    this.initPromise = new Promise<void>((resolve, reject) => {
      if (typeof indexedDB === 'undefined') {
        reject(new Error('IndexedDB not available'));
        return;
      }
      const req = indexedDB.open(this.DB_NAME, this.DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(this.STORE_SESSIONS)) {
          db.createObjectStore(this.STORE_SESSIONS);
        }
        if (!db.objectStoreNames.contains(this.STORE_METADATA)) {
          db.createObjectStore(this.STORE_METADATA);
        }
      };
      req.onsuccess = () => {
        this.db = req.result;
        resolve();
      };
      req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
    });
    return this.initPromise;
  }

  private async tx<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
    await this.init();
    if (!this.db) throw new Error('IndexedDB not initialized');
    return new Promise<T>((resolve, reject) => {
      const tx = this.db!.transaction(store, mode);
      const req = fn(tx.objectStore(store));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error('IndexedDB tx failed'));
    });
  }

  saveSession(id: string, data: SessionData): Promise<IDBValidKey> {
    return this.tx(this.STORE_SESSIONS, 'readwrite', s => s.put(data, id));
  }

  loadSession(id: string): Promise<SessionData | null> {
    return this.tx<SessionData | undefined>(this.STORE_SESSIONS, 'readonly', s => s.get(id))
      .then(v => v ?? null);
  }

  deleteSession(id: string): Promise<undefined> {
    return this.tx(this.STORE_SESSIONS, 'readwrite', s => s.delete(id)) as Promise<undefined>;
  }

  loadAllSessions(): Promise<SessionData[]> {
    return this.tx<SessionData[]>(this.STORE_SESSIONS, 'readonly', s => s.getAll());
  }

  saveMetadata(list: SessionMetadata[]): Promise<IDBValidKey> {
    return this.tx(this.STORE_METADATA, 'readwrite', s => s.put(list, 'all'));
  }

  loadMetadata(): Promise<SessionMetadata[]> {
    return this.tx<SessionMetadata[] | undefined>(this.STORE_METADATA, 'readonly', s => s.get('all'))
      .then(v => v ?? []);
  }
}

/**
 * IndexedDB 기반 세션 관리.
 * 공개 동기 API는 인메모리 캐시에서 즉시 반환, 영속화는 IndexedDB로 fire-and-forget + 재시도.
 */

export interface SessionMetadata {
  id: string;
  name: string;
  startTime: Date;
  endTime?: Date;
  duration: number; // seconds
  deviceName: string;
  deviceId: string;
  samplingRate: number;
  dataCount: {
    eeg: number;
    ppg: number;
    acc: number;
    eegProcessed: number;
    ppgProcessed: number;
    accProcessed: number;
  };
  totalSamples?: number; // 전체 샘플 수
  estimatedSize?: number; // 추정 파일 크기 (bytes)
  notes?: string;
  saveOptions: {
    eegRaw: boolean;
    ppgRaw: boolean;
    accRaw: boolean;
    eegProcessed: boolean;
    ppgProcessed: boolean;
    accProcessed: boolean;
  };
}

export interface SessionData {
  metadata: SessionMetadata;
  eegData: EEGDataPoint[];
  ppgData: PPGDataPoint[];
  accData: ACCDataPoint[];
  systemLogs: string[]; // CSV 형태의 시스템 로그
}

type SensorType = 'eeg' | 'ppg' | 'acc';

export class SessionManager {
  // IndexedDB 영속화 — localStorage 5~10MB 한계 제거.
  private readonly db = new SessionDB();
  private readonly cachedMetadata: Map<string, SessionMetadata> = new Map();
  private readonly cachedSessions: Map<string, SessionData> = new Map();
  private initPromise: Promise<void>;

  private currentSession: SessionData | null = null;
  private autoSaveInterval: ReturnType<typeof setInterval> | null = null;
  private readonly AUTO_SAVE_INTERVAL = 5000; // 5초마다 IndexedDB flush + 스트리밍 flush

  // 스트리밍 저장 (녹화 중 실시간 파일 쓰기) — 5초 또는 1000샘플 도달 시 flush
  private readonly streamFlushSampleThreshold = 1000;
  private streamingEnabled = false;
  private streamBufferEEG: EEGDataPoint[] = [];
  private streamBufferPPG: PPGDataPoint[] = [];
  private streamBufferACC: ACCDataPoint[] = [];

  private isSaving = false;
  private pendingSave = false;

  constructor() {
    this.initPromise = this.db.init()
      .then(async () => {
        const md = await this.db.loadMetadata();
        for (const m of md) {
          this.cachedMetadata.set(m.id, this.hydrateMetadata(m));
        }
        const sessions = await this.db.loadAllSessions();
        for (const s of sessions) {
          this.cachedSessions.set(s.metadata.id, this.hydrateSession(s));
        }
      })
      .catch(err => logger.error('SessionManager init 실패:', err));
  }

  /** IndexedDB 로딩 완료 대기 — 초기 부팅 시 명시적 await가 필요한 경우 사용 */
  async ready(): Promise<void> {
    return this.initPromise;
  }

  private hydrateMetadata(m: SessionMetadata): SessionMetadata {
    return {
      ...m,
      startTime: m.startTime ? new Date(m.startTime) : new Date(),
      endTime: m.endTime ? new Date(m.endTime) : undefined,
      duration: m.duration || 0,
      deviceName: m.deviceName || 'Unknown Device',
      deviceId: m.deviceId || 'unknown',
      samplingRate: m.samplingRate || 250,
      dataCount: {
        eeg: m.dataCount?.eeg || 0,
        ppg: m.dataCount?.ppg || 0,
        acc: m.dataCount?.acc || 0,
        eegProcessed: m.dataCount?.eegProcessed || 0,
        ppgProcessed: m.dataCount?.ppgProcessed || 0,
        accProcessed: m.dataCount?.accProcessed || 0
      },
      saveOptions: m.saveOptions || {
        eegRaw: true, ppgRaw: true, accRaw: true,
        eegProcessed: true, ppgProcessed: true, accProcessed: true
      }
    };
  }

  private hydrateSession(s: SessionData): SessionData {
    return {
      metadata: this.hydrateMetadata(s.metadata),
      eegData: s.eegData ?? [],
      ppgData: s.ppgData ?? [],
      accData: s.accData ?? [],
      systemLogs: s.systemLogs ?? []
    };
  }

  /**
   * 저장 실패 시 toast + 지수 backoff(1s/2s/3s) 재시도.
   * 마지막 시도 실패 시 토스트 + 에러 전파.
   */
  private async saveWithRetry(saveFn: () => Promise<void>, label: string, maxRetries = 3): Promise<void> {
    for (let i = 0; i < maxRetries; i++) {
      try {
        await saveFn();
        return;
      } catch (error) {
        if (i === maxRetries - 1) {
          const msg = error instanceof Error ? error.message : String(error);
          logger.error(`❌ ${label} 최종 실패:`, error);
          this.notifySaveError(label, msg);
          throw error;
        }
        await new Promise(r => setTimeout(r, 1000 * (i + 1)));
      }
    }
  }

  private notifySaveError(label: string, description: string): void {
    void import('sonner').then(({ toast }) => {
      toast.error(`저장 실패: ${label}`, { description, duration: 5000 });
    }).catch(() => {
      // toast 모듈 로드 실패 시 콘솔로 대체
    });
  }

  /**
   * 새 세션 시작
   */
  startSession(
    deviceName: string, 
    deviceId: string, 
    sessionName?: string,
    saveOptions: { eegRaw: boolean; ppgRaw: boolean; accRaw: boolean; eegProcessed: boolean; ppgProcessed: boolean; accProcessed: boolean } = { eegRaw: true, ppgRaw: true, accRaw: true, eegProcessed: true, ppgProcessed: true, accProcessed: true },
    timezone: TimezoneType = 'system'
  ): string {
    // 기존 세션이 있으면 종료
    if (this.currentSession) {
      this.endSession();
    }

    const sessionId = this.generateSessionId();
    const now = createSessionStartTime(timezone);

    const metadata: SessionMetadata = {
      id: sessionId,
      name: sessionName || `Session ${now.toLocaleDateString()} ${now.toLocaleTimeString()}`,
      startTime: now,
      duration: 0,
      deviceName,
      deviceId,
      samplingRate: 250, // LINK BAND 기본값
      dataCount: {
        eeg: 0,
        ppg: 0,
        acc: 0,
        eegProcessed: 0,
        ppgProcessed: 0,
        accProcessed: 0
      },
      saveOptions
    };

    this.currentSession = {
      metadata,
      eegData: [],
      ppgData: [],
      accData: [],
      systemLogs: []
    };

    // 메모리 캐시에 즉시 반영 (UI가 sync로 조회)
    this.cachedSessions.set(sessionId, this.currentSession);
    this.cachedMetadata.set(sessionId, metadata);

    // 스트리밍 저장 활성화 — IndexedDB가 항상 사용 가능
    this.streamingEnabled = true;
    this.streamBufferEEG = [];
    this.streamBufferPPG = [];
    this.streamBufferACC = [];

    // 자동 저장 시작 (5초 주기)
    this.startAutoSave();

    return sessionId;
  }

  /**
   * 현재 세션 종료
   */
  endSession(): boolean {
    if (!this.currentSession) {
      logger.warn('종료할 활성 세션이 없습니다');
      return false;
    }

    // 종료 시간 설정
    const endTime = new Date();
    this.currentSession.metadata.endTime = endTime;
    this.currentSession.metadata.duration = Math.floor(
      (endTime.getTime() - this.currentSession.metadata.startTime.getTime()) / 1000
    );

    // 남은 스트림 버퍼 flush
    this.flushAllStreamBuffers();
    this.streamingEnabled = false;

    // 최종 저장 (IndexedDB)
    void this.saveCurrentSession();

    // 자동 저장 중지
    this.stopAutoSave();

    this.currentSession = null;

    return true;
  }

  /**
   * EEG 데이터 추가
   */
  addEEGData(data: EEGDataPoint[]): void {
    if (!this.currentSession || !this.currentSession.metadata.saveOptions.eegRaw) return;
    // IndexedDB가 충분히 크므로 MAX_DATA_POINTS 제한 제거
    this.currentSession.eegData.push(...data);
    this.currentSession.metadata.dataCount.eeg = this.currentSession.eegData.length;
    if (this.streamingEnabled) {
      this.streamBufferEEG.push(...data);
      if (this.streamBufferEEG.length >= this.streamFlushSampleThreshold) this.flushStreamBuffer('eeg');
    }
  }

  /**
   * PPG 데이터 추가
   */
  addPPGData(data: PPGDataPoint[]): void {
    if (!this.currentSession || !this.currentSession.metadata.saveOptions.ppgRaw) return;
    this.currentSession.ppgData.push(...data);
    this.currentSession.metadata.dataCount.ppg = this.currentSession.ppgData.length;
    if (this.streamingEnabled) {
      this.streamBufferPPG.push(...data);
      if (this.streamBufferPPG.length >= this.streamFlushSampleThreshold) this.flushStreamBuffer('ppg');
    }
  }

  /**
   * 가속도계 데이터 추가
   */
  addACCData(data: ACCDataPoint[]): void {
    if (!this.currentSession || !this.currentSession.metadata.saveOptions.accRaw) return;
    this.currentSession.accData.push(...data);
    this.currentSession.metadata.dataCount.acc = this.currentSession.accData.length;
    if (this.streamingEnabled) this.enqueueStream('acc', data);
  }

  /**
   * 스트림 버퍼에 적재 후 1000샘플 도달 시 즉시 flush.
   * 5초 주기 flush는 startAutoSave()의 타이머가 처리.
   */
  private enqueueStream(type: SensorType, data: EEGDataPoint[] | PPGDataPoint[] | ACCDataPoint[]): void {
    const buf = this.getStreamBuffer(type);
    buf.push(...(data as never[]));
    if (buf.length >= this.streamFlushSampleThreshold) {
      this.flushStreamBuffer(type);
    }
  }

  private getStreamBuffer(type: SensorType): unknown[] {
    switch (type) {
      case 'eeg': return this.streamBufferEEG as unknown[];
      case 'ppg': return this.streamBufferPPG as unknown[];
      case 'acc': return this.streamBufferACC as unknown[];
    }
  }

  private clearStreamBuffer(type: SensorType): void {
    switch (type) {
      case 'eeg': this.streamBufferEEG = []; break;
      case 'ppg': this.streamBufferPPG = []; break;
      case 'acc': this.streamBufferACC = []; break;
    }
  }

  private flushStreamBuffer(type: SensorType): void {
    const buf = this.getStreamBuffer(type);
    if (buf.length === 0) return;
    // IndexedDB auto-save가 5초마다 처리하므로 별도 파일시스템 flush 불필요
    this.clearStreamBuffer(type);
  }

  private flushAllStreamBuffers(): void {
    this.flushStreamBuffer('eeg');
    this.flushStreamBuffer('ppg');
    this.flushStreamBuffer('acc');
  }

  /**
   * 처리된 데이터 추가 (하위 호환성)
   */
  addProcessedData(data: ProcessedEEGData): void {
    // 🔧 processed 데이터 메소드 제거 - 복잡성으로 인해 제외
  }

  /**
   * 시스템 로그 추가
   */
  addSystemLog(logEntry: string): void {
    if (!this.currentSession) return;

    this.currentSession.systemLogs.push(logEntry);

    // 시스템 로그는 더 많이 보관 (최대 10000개)
    if (this.currentSession.systemLogs.length > 10000) {
      this.currentSession.systemLogs.shift();
    }
  }

  /**
   * 현재 세션 정보 반환
   */
  getCurrentSession(): SessionData | null {
    return this.currentSession;
  }

  /**
   * 모든 세션 메타데이터 반환 (IndexedDB 인메모리 캐시)
   */
  getAllSessions(): SessionMetadata[] {
    return Array.from(this.cachedMetadata.values());
  }

  /** 진행률 콜백 지원 비동기 메타데이터 로드 — IndexedDB → 캐시 재동기화 */
  async loadSessions(onProgress?: (current: number, total: number) => void): Promise<SessionMetadata[]> {
    await this.initPromise;
    const md = await this.db.loadMetadata();
    this.cachedMetadata.clear();
    md.forEach((m, i) => {
      this.cachedMetadata.set(m.id, this.hydrateMetadata(m));
      onProgress?.(i + 1, md.length);
    });
    return this.getAllSessions();
  }

  /**
   * 특정 세션 로드 (인메모리 캐시 우선)
   * @deprecated 동기 로드 — 신규 호출은 loadSessionAsync 사용 권장
   */
  loadSession(sessionId: string): SessionData | null {
    const cached = this.cachedSessions.get(sessionId);
    if (cached) return cached;
    logger.warn(`세션을 메모리 캐시에서 찾을 수 없습니다 (IndexedDB 초기화 중일 수 있음): ${sessionId}`);
    // 비동기로 IndexedDB에서 로드 시도 (캐시 워밍)
    void this.db.loadSession(sessionId).then(s => {
      if (s) this.cachedSessions.set(sessionId, this.hydrateSession(s));
    }).catch(err => logger.error(`IndexedDB loadSession 실패: ${sessionId}`, err));
    return null;
  }

  /** IndexedDB에서 비동기로 세션 로드 */
  async loadSessionAsync(sessionId: string): Promise<SessionData | null> {
    await this.initPromise;
    const cached = this.cachedSessions.get(sessionId);
    if (cached) return cached;
    const s = await this.db.loadSession(sessionId);
    if (!s) return null;
    const hydrated = this.hydrateSession(s);
    this.cachedSessions.set(sessionId, hydrated);
    return hydrated;
  }


  /**
   * 세션 삭제 (캐시 즉시 반영 + IndexedDB fire-and-forget)
   */
  deleteSession(sessionId: string): boolean {
    try {
      this.cachedSessions.delete(sessionId);
      this.cachedMetadata.delete(sessionId);
      void this.saveWithRetry(
        async () => { await this.db.deleteSession(sessionId); },
        `세션 삭제(${sessionId})`
      );
      void this.persistMetadata();
      return true;
    } catch (error) {
      logger.error(`세션 삭제 실패: ${sessionId}`, error);
      return false;
    }
  }

  /**
   * CSV 내보내기
   */
  exportToCSV(sessionId: string, dataType: 'eeg' | 'ppg' | 'acc' | 'system' = 'eeg'): string | null {
    const session = sessionId === 'current' ? this.currentSession : this.loadSession(sessionId);
    if (!session) {
      logger.error('내보낼 세션을 찾을 수 없습니다');
      return null;
    }

    try {
      let csvContent = '';

      switch (dataType) {
        case 'eeg':
          csvContent = this.generateEEGCSV(session.eegData, session.metadata);
          break;
        case 'ppg':
          csvContent = this.generatePPGCSV(session.ppgData, session.metadata);
          break;
        case 'acc':
          csvContent = this.generateACCCSV(session.accData, session.metadata);
          break;
        // 🔧 processed 데이터 케이스 제거 - 복잡성으로 인해 제외
        case 'system':
          csvContent = this.generateSystemCSV(session.systemLogs, session.metadata);
          break;
        default:
          throw new Error(`지원되지 않는 데이터 타입: ${dataType}`);
      }

      return csvContent;
    } catch (error) {
      logger.error('CSV 내보내기 실패:', error);
      return null;
    }
  }

  /**
   * 모든 데이터를 ZIP 파일로 다운로드
   */
  async downloadAllAsZip(sessionId: string, targetDirHandle?: FileSystemDirectoryHandle): Promise<boolean> {
    logger.debug(`🔧 SessionManager.downloadAllAsZip 호출됨 - sessionId: ${sessionId}`);
    logger.debug(`🔧 Target directory handle:`, targetDirHandle ? `${targetDirHandle.name}` : 'undefined (기본 다운로드)');
    
    let session: SessionData | null = null;
    
    if (sessionId === 'current') {
      // 현재 세션은 localStorage에서 가져오기
      session = this.currentSession;
    } else {
      // 저장된 세션은 먼저 localStorage에서 시도
      session = this.loadSession(sessionId);
      
      if (!session) {
        // localStorage에 없으면 StorageStore에서 메타데이터 가져와서 가상 세션 생성
        logger.debug(`🔧 localStorage에서 세션을 찾을 수 없음, StorageStore에서 메타데이터 검색: ${sessionId}`);
        try {
          const { useStorageStore } = await import('../stores/storageStore');
          const storageStore = useStorageStore.getState();
          const sessionInfo = storageStore.sessions.find(s => s.id === sessionId);
          
          if (sessionInfo) {
            logger.debug(`🔧 StorageStore에서 세션 메타데이터 발견: ${sessionInfo.name}`);
            // StorageStore 세션을 SessionData 형태로 변환
            session = {
              metadata: {
                id: sessionInfo.id,
                name: sessionInfo.name,
                startTime: new Date(sessionInfo.date + ' ' + sessionInfo.time),
                endTime: undefined, // StorageStore에서는 endTime 정보가 없음
                duration: this.parseDurationString(sessionInfo.duration),
                deviceName: sessionInfo.deviceName || 'Unknown Device',
                deviceId: sessionInfo.deviceId || 'unknown',
                samplingRate: 250,
                dataCount: {
                  eeg: 0,
                  ppg: 0,
                  acc: 0,
                  eegProcessed: 0,
                  ppgProcessed: 0,
                  accProcessed: 0
                },
                saveOptions: {
                  eegRaw: true,
                  ppgRaw: true,
                  accRaw: true,
                  eegProcessed: true,
                  ppgProcessed: true,
                  accProcessed: true
                }
              },
              eegData: [],
              ppgData: [],
              accData: [],
              systemLogs: []
            };
          }
        } catch (error) {
          logger.error('❌ StorageStore에서 세션 정보 가져오기 실패:', error);
        }
      }
    }
    
    if (!session) {
      logger.error('❌ SessionManager.downloadAllAsZip - 세션을 찾을 수 없습니다');
      return false;
    }

    try {
      logger.debug(`🔧 SessionManager.downloadAllAsZip - 세션 발견: ${session.metadata.name}`);
      const zip = new JSZip();
      
      // 세션 정보 파일 추가
      const sessionInfo = this.generateSessionInfoText(session.metadata);
      zip.file('session_info.txt', sessionInfo);

      // 세션 메타데이터 JSON 파일 추가
      const metadataJson = JSON.stringify(session.metadata, null, 2);
      zip.file('metadata.json', metadataJson);

      // Raw 데이터 파일들 추가 (StorageStore 세션의 경우 파일 시스템에서 확인해야 하므로 항상 시도)
      const isStorageStoreSession = sessionId !== 'current' && session.eegData.length === 0 && session.ppgData.length === 0 && session.accData.length === 0;
      logger.debug(`🔧 세션 타입 확인: ${isStorageStoreSession ? 'StorageStore 세션' : 'localStorage 세션'}`);
      
      const rawDataTypes: Array<{ type: 'eeg' | 'ppg' | 'acc' | 'system', filename: string, hasData: boolean }> = [
        { type: 'eeg', filename: 'raw-data/eeg_raw_data.csv', hasData: isStorageStoreSession || session.eegData.length > 0 },
        { type: 'ppg', filename: 'raw-data/ppg_raw_data.csv', hasData: isStorageStoreSession || session.ppgData.length > 0 },
        { type: 'acc', filename: 'raw-data/acc_raw_data.csv', hasData: isStorageStoreSession || session.accData.length > 0 },
        { type: 'system', filename: 'system_logs.csv', hasData: isStorageStoreSession || session.systemLogs.length > 0 }
      ];

      // Raw 데이터 파일들 추가 (실제 저장된 파일들 우선, 없으면 localStorage 데이터 사용)
      await this.addRawDataToZip(zip, session.metadata, rawDataTypes, sessionId);

      // Analytics metrics 파일들 추가 (StreamingStorageService에서 저장된 파일들)
      await this.addAnalyticsMetricsToZip(zip, session.metadata);

      // ZIP 파일 생성
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipFileName = `${session.metadata.name}_complete_data_${Date.now()}.zip`;

      // 사용자가 선택한 폴더에 저장 또는 기본 다운로드
      if (targetDirHandle) {
        logger.debug(`🔧 사용자 선택 폴더에 저장: ${targetDirHandle.name}`);
        try {
          // 사용자가 선택한 폴더에 ZIP 파일 저장
          const fileHandle = await targetDirHandle.getFileHandle(zipFileName, { create: true });
          const writable = await fileHandle.createWritable();
          await writable.write(zipBlob);
          await writable.close();
          logger.debug(`✅ ZIP 파일이 선택한 폴더에 저장됨: ${zipFileName}`);
        } catch (writeError) {
          logger.error('❌ 선택한 폴더에 파일 저장 실패, 기본 다운로드로 대체:', writeError);
          // 폴더 저장 실패 시 기본 다운로드로 대체
          this.downloadZipBlob(zipBlob, zipFileName);
        }
      } else {
        logger.debug(`🔧 기본 다운로드 폴더에 저장`);
        // 기본 다운로드 방식 사용
        this.downloadZipBlob(zipBlob, zipFileName);
      }

      logger.debug('✅ ZIP 파일 다운로드 완료');
      return true;
    } catch (error) {
      logger.error('ZIP 다운로드 실패:', error);
      return false;
    }
  }

  /**
   * ZIP blob을 기본 다운로드 방식으로 다운로드
   */
  private downloadZipBlob(zipBlob: Blob, fileName: string): void {
    const link = document.createElement('a');
    const url = URL.createObjectURL(zipBlob);
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Analytics metrics 파일들을 ZIP에 추가
   */
  private async addAnalyticsMetricsToZip(zip: JSZip, metadata: SessionMetadata): Promise<void> {
    try {
      logger.debug('Analytics metrics: IndexedDB 기반이므로 샘플 metrics 생성');
      await this.addSampleAnalyticsMetrics(zip, metadata);
    } catch (error) {
      logger.error('Analytics metrics 파일 추가 실패:', error);
      await this.addSampleAnalyticsMetrics(zip, metadata);
    }
  }

  /**
   * Raw data 파일들을 ZIP에 추가 (StorageStore 파일 시스템 우선)
   */
  private async addRawDataToZip(
    zip: JSZip, 
    metadata: SessionMetadata, 
    rawDataTypes: Array<{ type: 'eeg' | 'ppg' | 'acc' | 'system', filename: string, hasData: boolean }>,
    sessionId: string
  ): Promise<void> {
    try {
      logger.debug('Raw data: IndexedDB 데이터를 CSV로 변환하여 ZIP에 추가');
      await this.addLocalStorageRawData(zip, rawDataTypes, sessionId);
    } catch (error) {
      logger.error('Raw data 파일 추가 실패:', error);
      await this.addLocalStorageRawData(zip, rawDataTypes, sessionId);
    }
  }

  /**
   * localStorage에서 Raw data를 CSV로 변환하여 ZIP에 추가 (fallback)
   */
  private async addLocalStorageRawData(
    zip: JSZip, 
    rawDataTypes: Array<{ type: 'eeg' | 'ppg' | 'acc' | 'system', filename: string, hasData: boolean }>,
    sessionId: string
  ): Promise<void> {
    logger.debug(`🔧 localStorage 데이터 fallback 시작 - sessionId: ${sessionId}`);
    
    // StorageStore 세션의 경우 빈 CSV 파일이라도 생성
    const isStorageStoreSession = sessionId !== 'current';
    
    for (const dataType of rawDataTypes) {
      if (dataType.hasData) {
        const csvContent = this.exportToCSV(sessionId, dataType.type);
        if (csvContent && csvContent.length > 0) {
          zip.file(dataType.filename, csvContent);
          logger.debug(`✅ ${dataType.filename} 파일 추가됨 (localStorage) (${csvContent.length} bytes)`);
        } else if (isStorageStoreSession) {
          // StorageStore 세션의 경우 빈 CSV 파일이라도 헤더와 함께 생성
          const emptyCSV = this.generateEmptyCSV(dataType.type);
          zip.file(dataType.filename, emptyCSV);
          logger.debug(`⚠️ ${dataType.filename} 빈 파일 추가됨 (StorageStore 세션)`);
        }
      }
    }
  }

  /**
   * 빈 CSV 파일 생성 (헤더만 포함)
   */
  private generateEmptyCSV(dataType: 'eeg' | 'ppg' | 'acc' | 'system'): string {
    switch (dataType) {
      case 'eeg':
        return 'timestamp,ch1,ch2,ch3,ch4,ch5,ch6,ch7,ch8,battery,temperature,accelerometer_x,accelerometer_y,accelerometer_z,gyroscope_x,gyroscope_y,gyroscope_z\n';
      case 'ppg':
        return 'timestamp,ppg_red,ppg_ir,ppg_green,ambient_light,temperature\n';
      case 'acc':
        return 'timestamp,x,y,z,magnitude\n';
      case 'system':
        return 'timestamp,event,details\n';
      default:
        return 'timestamp,data\n';
    }
  }

  /**
   * 샘플 Analytics metrics 파일들을 ZIP에 추가 (fallback)
   */
  private async addSampleAnalyticsMetrics(zip: JSZip, metadata: SessionMetadata): Promise<void> {
    const analyticsDir = 'analysis-metrics/';
    
    // EEG Analytics Metrics 샘플 생성
    const eegAnalyticsHeader = `# LINK BAND EEG Analysis Metrics - Session: ${metadata.name}\n`;
    const eegAnalyticsContent = eegAnalyticsHeader + 
      `timestamp,total_power,emotional_balance,attention,cognitive_load,focus_index,relaxation_index,stress_index,hemispheric_balance,emotional_stability,attention_level,meditation_level,` +
      `ma_total_power,ma_emotional_balance,ma_attention,ma_cognitive_load,ma_focus_index,ma_relaxation_index,ma_stress_index,ma_hemispheric_balance,ma_emotional_stability,ma_attention_level,ma_meditation_level\n`;
    
    // PPG Analytics Metrics 샘플 생성
    const ppgAnalyticsHeader = `# LINK BAND PPG Analysis Metrics - Session: ${metadata.name}\n`;
    const ppgAnalyticsContent = ppgAnalyticsHeader + 
      `timestamp,bpm,sdnn,rmssd,pnn50,lf_power,hf_power,lf_hf_ratio,stress_index,spo2,` +
      `ma_bpm,ma_sdnn,ma_rmssd,ma_pnn50,ma_lf_power,ma_hf_power,ma_lf_hf_ratio,ma_stress_index,ma_spo2\n`;
    
    // ACC Analytics Metrics 샘플 생성  
    const accAnalyticsHeader = `# LINK BAND ACC Analysis Metrics - Session: ${metadata.name}\n`;
    const accAnalyticsContent = accAnalyticsHeader + 
      `timestamp,activity_state,activity_level,stability,avg_movement,std_movement,max_movement,tilt_angle,balance,` +
      `ma_activity_level,ma_stability,ma_avg_movement,ma_std_movement,ma_max_movement,ma_tilt_angle,ma_balance\n`;

    // Analytics metrics 파일들을 ZIP에 추가
    zip.file(analyticsDir + 'eeg-analysis-metrics.csv', eegAnalyticsContent);
    zip.file(analyticsDir + 'ppg-analysis-metrics.csv', ppgAnalyticsContent);
    zip.file(analyticsDir + 'acc-analysis-metrics.csv', accAnalyticsContent);
    
    logger.debug('✅ 샘플 Analytics metrics 파일들이 ZIP에 추가됨');
  }

  /**
   * CSV 파일 다운로드
   */
  downloadCSV(sessionId: string, dataType: 'eeg' | 'ppg' | 'acc' | 'system' = 'eeg'): boolean {
    const csvContent = this.exportToCSV(sessionId, dataType);
    if (!csvContent) return false;

    try {
      const session = sessionId === 'current' ? this.currentSession : this.loadSession(sessionId);
      if (!session) return false;

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${session.metadata.name}_${dataType}_${Date.now()}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }

  
      return true;
    } catch (error) {
      logger.error('CSV 다운로드 실패:', error);
      return false;
    }
  }

  /**
   * 저장소 용량 추정.
   * IndexedDB는 quota 정확 산출 비용이 커 메모리 캐시 기반 추정값을 반환.
   * 비동기 정확값이 필요하면 getStorageInfoAsync() 사용.
   */
  getStorageInfo(): { used: number; available: number; sessions: number } {
    const sessions = this.cachedMetadata.size;
    let used = 0;
    this.cachedSessions.forEach(s => {
      used += (s.eegData.length + s.ppgData.length + s.accData.length) * 32;
    });
    // IndexedDB는 사실상 제한이 매우 큼. 1GB 가정.
    const available = Math.max(0, 1024 * 1024 * 1024 - used);
    return { used, available, sessions };
  }

  async getStorageInfoAsync(): Promise<{ used: number; available: number; sessions: number }> {
    try {
      if (typeof navigator !== 'undefined' && navigator.storage?.estimate) {
        const est = await navigator.storage.estimate();
        return {
          used: est.usage ?? 0,
          available: Math.max(0, (est.quota ?? 0) - (est.usage ?? 0)),
          sessions: this.cachedMetadata.size,
        };
      }
    } catch {
      // ignore
    }
    return this.getStorageInfo();
  }

  /** IndexedDB 무제한에 가깝지만, 메타데이터 비대화 방지를 위해 보존 정책은 호출자 책임 */
  cleanupOldSessions(): void {
    // no-op (localStorage 시절의 MAX_SESSIONS 제한 제거)
  }

  // Private methods

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private generateSessionInfoText(metadata: SessionMetadata): string {
    let info = `LINK BAND Session Information\n`;
    info += `================================\n\n`;
    info += `Session Name: ${metadata.name}\n`;
    info += `Session ID: ${metadata.id}\n`;
    info += `Device: ${metadata.deviceName} (${metadata.deviceId})\n`;
    info += `Start Time: ${metadata.startTime.toISOString()}\n`;
    if (metadata.endTime) {
      info += `End Time: ${metadata.endTime.toISOString()}\n`;
    }
    info += `Duration: ${metadata.duration} seconds\n`;
    info += `Sampling Rate: ${metadata.samplingRate} Hz\n\n`;
    
    info += `Data Summary:\n`;
    info += `-------------\n`;
    info += `EEG Raw Data: ${metadata.dataCount.eeg.toLocaleString()} points\n`;
    info += `PPG Raw Data: ${metadata.dataCount.ppg.toLocaleString()} points\n`;
    info += `ACC Raw Data: ${metadata.dataCount.acc.toLocaleString()} points\n`;
    info += `EEG Processed Data: ${metadata.dataCount.eegProcessed.toLocaleString()} points\n`;
    info += `PPG Processed Data: ${metadata.dataCount.ppgProcessed.toLocaleString()} points\n`;
    info += `ACC Processed Data: ${metadata.dataCount.accProcessed.toLocaleString()} points\n\n`;
    
    info += `Save Options:\n`;
    info += `-------------\n`;
    info += `EEG Raw: ${metadata.saveOptions.eegRaw ? 'Enabled' : 'Disabled'}\n`;
    info += `PPG Raw: ${metadata.saveOptions.ppgRaw ? 'Enabled' : 'Disabled'}\n`;
    info += `ACC Raw: ${metadata.saveOptions.accRaw ? 'Enabled' : 'Disabled'}\n`;
    info += `EEG Processed: ${metadata.saveOptions.eegProcessed ? 'Enabled' : 'Disabled'}\n`;
    info += `PPG Processed: ${metadata.saveOptions.ppgProcessed ? 'Enabled' : 'Disabled'}\n`;
    info += `ACC Processed: ${metadata.saveOptions.accProcessed ? 'Enabled' : 'Disabled'}\n\n`;
    
    if (metadata.notes) {
      info += `Notes:\n`;
      info += `------\n`;
      info += `${metadata.notes}\n\n`;
    }
    
    info += `Generated: ${new Date().toISOString()}\n`;
    
    return info;
  }

  private startAutoSave(): void {
    this.stopAutoSave();

    this.autoSaveInterval = setInterval(() => {
      const endTimer = performanceMonitor.startTimer('SessionManager.autoSave');
      try {
        // 5초 주기 스트림 flush — 1000샘플 미달이라도 시간 도달 시 강제 flush
        if (this.streamingEnabled) this.flushAllStreamBuffers();

        if (this.currentSession && !this.isSaving) {
          void this.saveCurrentSessionOptimized();
        } else if (this.currentSession && this.isSaving) {
          this.pendingSave = true;
        }
      } finally {
        endTimer();
      }
    }, this.AUTO_SAVE_INTERVAL);
  }

  private stopAutoSave(): void {
    if (this.autoSaveInterval) {
      clearInterval(this.autoSaveInterval);
      this.autoSaveInterval = null;
    }
    this.pendingSave = false;
  }

  /** IndexedDB로의 비동기 저장 (UI 블로킹 없음) + 3회 재시도 */
  private async saveCurrentSessionOptimized(): Promise<void> {
    if (!this.currentSession || this.isSaving) return;
    const endTimer = performanceMonitor.startTimer('SessionManager.saveOptimized');
    this.isSaving = true;
    try {
      await this.saveCurrentSession();
    } catch (error) {
      logger.error('세션 저장 실패(최적화 경로):', error);
    } finally {
      this.isSaving = false;
      endTimer();
      if (this.pendingSave) {
        this.pendingSave = false;
        setTimeout(() => void this.saveCurrentSessionOptimized(), 1000);
      }
    }
  }

  /**
   * 현재 세션을 IndexedDB에 저장.
   * 메모리 캐시는 동기 반영, IndexedDB는 비동기 (UI 블로킹 없음).
   * 실패 시 saveWithRetry가 1s/2s/3s backoff + toast 알림.
   */
  private async saveCurrentSession(): Promise<void> {
    if (!this.currentSession) return;

    const now = new Date();
    this.currentSession.metadata.duration = Math.floor(
      (now.getTime() - this.currentSession.metadata.startTime.getTime()) / 1000
    );
    this.currentSession.metadata.dataCount = {
      eeg: this.currentSession.eegData.length,
      ppg: this.currentSession.ppgData.length,
      acc: this.currentSession.accData.length,
      eegProcessed: 0,
      ppgProcessed: 0,
      accProcessed: 0
    };

    const sessionData = this.currentSession;
    this.cachedSessions.set(sessionData.metadata.id, sessionData);
    this.cachedMetadata.set(sessionData.metadata.id, sessionData.metadata);

    await this.saveWithRetry(
      async () => { await this.db.saveSession(sessionData.metadata.id, sessionData); },
      `세션 저장(${sessionData.metadata.name})`
    );
    await this.persistMetadata();
  }

  private async persistMetadata(): Promise<void> {
    const list = Array.from(this.cachedMetadata.values());
    await this.saveWithRetry(
      async () => { await this.db.saveMetadata(list); },
      '세션 메타데이터 저장'
    );
  }

  /**
   * 지속시간 문자열을 초 단위로 변환
   */
  private parseDurationString(durationStr: string): number {
    if (!durationStr) return 0;
    
    // "16초", "1분 30초", "1시간 5분" 등의 형태를 파싱
    const timeUnits = [
      { unit: '시간', multiplier: 3600 },
      { unit: '분', multiplier: 60 },
      { unit: '초', multiplier: 1 }
    ];
    
    let totalSeconds = 0;
    
    for (const { unit, multiplier } of timeUnits) {
      const regex = new RegExp(`(\\d+)${unit}`);
      const match = durationStr.match(regex);
      if (match) {
        totalSeconds += parseInt(match[1]) * multiplier;
      }
    }
    
    return totalSeconds || 0;
  }


  private generateEEGCSV(data: EEGDataPoint[], metadata: SessionMetadata): string {
    let csv = `# LINK BAND EEG Data Export\n`;
    csv += `# Session: ${metadata.name}\n`;
    csv += `# Device: ${metadata.deviceName} (${metadata.deviceId})\n`;
    csv += `# Start Time: ${metadata.startTime.toISOString()}\n`;
    csv += `# Duration: ${metadata.duration}s\n`;
    csv += `# Sampling Rate: ${metadata.samplingRate}Hz\n`;
    csv += `# Data Points: ${data.length}\n`;
    csv += `#\n`;
    csv += `timestamp,fp1,fp2\n`;

    for (const point of data) {
      csv += `${point.timestamp},${point.fp1},${point.fp2}\n`;
    }

    return csv;
  }

  private generatePPGCSV(data: PPGDataPoint[], metadata: SessionMetadata): string {
    let csv = `# LINK BAND PPG Data Export\n`;
    csv += `# Session: ${metadata.name}\n`;
    csv += `timestamp,red,ir\n`;

    for (const point of data) {
      csv += `${point.timestamp},${point.red},${point.ir}\n`;
    }

    return csv;
  }

  private generateACCCSV(data: ACCDataPoint[], metadata: SessionMetadata): string {
    let csv = `# LINK BAND Accelerometer Data Export\n`;
    csv += `# Session: ${metadata.name}\n`;
    csv += `timestamp,x,y,z\n`;

    for (const point of data) {
      csv += `${point.timestamp},${point.x},${point.y},${point.z}\n`;
    }

    return csv;
  }

  private generateProcessedEEGCSV(data: ProcessedEEGData[], metadata: SessionMetadata): string {
    let csv = `# LINK BAND Processed EEG Data Export\n`;
    csv += `# Session: ${metadata.name}\n`;
    csv += `timestamp,delta,theta,alpha,beta,gamma,signal_quality,brain_state,confidence\n`;

    for (const point of data) {
      csv += `${point.timestamp},${point.bandPowers.delta},${point.bandPowers.theta},${point.bandPowers.alpha},${point.bandPowers.beta},${point.bandPowers.gamma},${point.signalQuality.overall},${point.brainState.currentState},${point.brainState.confidence}\n`;
    }

    return csv;
  }

  private generateProcessedPPGCSV(data: ProcessedPPGData[], metadata: SessionMetadata): string {
    let csv = `# LINK BAND Processed PPG Data Export\n`;
    csv += `# Session: ${metadata.name}\n`;
    csv += `timestamp,heart_rate,hrv,spo2,signal_quality\n`;

    for (const point of data) {
      csv += `${point.timestamp},${point.heartRate},${point.hrv},${point.spo2},${point.signalQuality}\n`;
    }

    return csv;
  }

  private generateProcessedACCCSV(data: ProcessedACCData[], metadata: SessionMetadata): string {
    let csv = `# LINK BAND Processed Accelerometer Data Export\n`;
    csv += `# Session: ${metadata.name}\n`;
    csv += `timestamp,magnitude,activity,steps,pitch,roll,yaw\n`;

    for (const point of data) {
      csv += `${point.timestamp},${point.magnitude},${point.activity},${point.steps},${point.orientation.pitch},${point.orientation.roll},${point.orientation.yaw}\n`;
    }

    return csv;
  }

  private generateSystemCSV(logs: string[], metadata: SessionMetadata): string {
    let csv = `# LINK BAND System Logs Export\n`;
    csv += `# Session: ${metadata.name}\n`;
    csv += `# Log Entries: ${logs.length}\n`;
    csv += `#\n`;

    for (const log of logs) {
      csv += log + '\n';
    }

    return csv;
  }

  /**
   * 성능 리포트 출력
   */
  getPerformanceReport() {
    return performanceMonitor.getAllMetrics();
  }

  /**
   * 성능 리포트 로깅
   */
  logPerformanceReport(): void {
    performanceMonitor.logPerformanceReport();
  }
}

// 싱글톤 인스턴스
export const sessionManager = new SessionManager(); 